import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import { User, Bell, CreditCard, Shield, ArrowLeft, ExternalLink, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getCurrentSubscription, createCustomerPortalSession } from '../lib/subscription-service';
import type { SubscriptionInfo } from '../lib/subscription-service';
import { validateLinkedInUrl } from '../lib/input-sanitization';

export default function Settings() {
  const { profile, user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);

  // Profile settings
  const [fullName, setFullName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [bio, setBio] = useState('');

  // Notification preferences
  const [emailOnFeedback, setEmailOnFeedback] = useState(true);
  const [emailOnReminder, setEmailOnReminder] = useState(true);
  const [emailWeeklyDigest, setEmailWeeklyDigest] = useState(true);
  const [emailMarketing, setEmailMarketing] = useState(false);

  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showCancelWarning, setShowCancelWarning] = useState(false);

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setJobTitle(profile.job_title || '');
      setCompany(profile.company || '');
      setLinkedinUrl(profile.linkedin_url || '');
      setBio(profile.bio || '');
    }
    loadNotificationPreferences();
    loadSubscription();
  }, [profile]);

  const loadSubscription = async () => {
    if (!user) return;

    try {
      const subscriptionInfo = await getCurrentSubscription(user.id);
      setSubscription(subscriptionInfo);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const loadNotificationPreferences = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setEmailOnFeedback(data.email_on_feedback_received);
        setEmailOnReminder(data.email_on_reminder);
        setEmailWeeklyDigest(data.email_weekly_digest);
        setEmailMarketing(data.email_marketing);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Validate LinkedIn URL if provided
    let normalizedLinkedinUrl = linkedinUrl.trim();
    if (normalizedLinkedinUrl) {
      const linkedinValidation = validateLinkedInUrl(normalizedLinkedinUrl);
      if (!linkedinValidation.valid) {
        showError(linkedinValidation.error || 'Invalid LinkedIn URL');
        return;
      }
      // Use the normalized URL from validation
      normalizedLinkedinUrl = linkedinValidation.normalizedUrl || normalizedLinkedinUrl;
    }

    setLoading(true);
    try {
      const { error } = await updateProfile({
        full_name: fullName,
        job_title: jobTitle,
        company: company,
        linkedin_url: normalizedLinkedinUrl || null,
        bio: bio
      });

      if (error) throw error;

      setSuccessMessage('Your profile has been updated successfully. Changes have been saved to your account.');
      setShowSuccessModal(true);
    } catch (error: any) {
      showError(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNotifications = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          email_on_feedback_received: emailOnFeedback,
          email_on_reminder: emailOnReminder,
          email_weekly_digest: emailWeeklyDigest,
          email_marketing: emailMarketing
        });

      if (error) throw error;

      setSuccessMessage('Your notification preferences have been updated successfully. You will receive emails based on your new settings.');
      setShowSuccessModal(true);
    } catch (error: any) {
      showError(error.message || 'Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = () => {
    setShowCancelWarning(true);
  };

  const proceedToBillingPortal = async () => {
    if (!user) return;

    setShowCancelWarning(false);
    setLoadingPortal(true);
    try {
      const url = await createCustomerPortalSession(user.id);
      if (url) {
        window.location.href = url;
      } else {
        showError('Unable to access billing portal. Your subscription may not have billing information. Please contact support if you need assistance.');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to open billing portal';
      if (errorMessage.includes('No active subscription')) {
        showError('No billing information found. You may be on the free plan. To upgrade, visit the Pricing page.');
      } else {
        showError(errorMessage);
      }
    } finally {
      setLoadingPortal(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'security', label: 'Security', icon: Shield }
  ];

  return (
    <>
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[24px] max-w-md w-full shadow-2xl p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#1F2937] mb-2">Thank You!</h2>
            <p className="text-[#6B7280] mb-6">
              {successMessage}
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] transition"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {showCancelWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[24px] max-w-lg w-full shadow-2xl p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">Manage Your Subscription</h2>
              <p className="text-[#6B7280] mb-4">
                You'll be redirected to Stripe's secure billing portal where you can:
              </p>
              <ul className="space-y-2 text-sm text-[#6B7280] mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-[#8CAFA8] mt-0.5">•</span>
                  <span>Update payment methods</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#8CAFA8] mt-0.5">•</span>
                  <span>View billing history and invoices</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#8CAFA8] mt-0.5">•</span>
                  <span>Cancel your subscription</span>
                </li>
              </ul>

              {subscription?.plan !== 'free' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-orange-800 font-medium mb-2">
                    If you cancel your subscription:
                  </p>
                  <ul className="space-y-1 text-sm text-orange-700">
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <span>You'll retain access until {subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'the end of your billing period'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <span>After that, you'll be downgraded to the Free plan</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <span>Your data will be preserved, but feature access will be limited</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <span>You can resubscribe at any time</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelWarning(false)}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold text-[#4B5563] hover:bg-gray-50 transition"
              >
                Go Back
              </button>
              <button
                onClick={proceedToBillingPortal}
                disabled={loadingPortal}
                className="flex-1 bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] disabled:opacity-50 transition flex items-center justify-center gap-2"
              >
                {loadingPortal ? 'Loading...' : (
                  <>
                    Continue
                    <ExternalLink className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-[#6B7280] hover:text-[#1F2937] transition"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-[#1F2937]">Settings</h1>
          <p className="text-[#6B7280] mt-1">Manage your account and preferences</p>
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          <div className="md:col-span-1">
            <nav className="bg-white rounded-lg shadow-sm p-2 space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition ${
                    activeTab === tab.id
                      ? 'bg-[#F0F4F3] text-[#7A9B94] font-semibold'
                      : 'text-[#4B5563] hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="md:col-span-3">
            <div className="bg-white rounded-lg shadow-sm p-8">
              {activeTab === 'profile' && (
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-[#1F2937] mb-4">Profile Information</h2>
                    <p className="text-[#6B7280] mb-6">Update your personal and professional details</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#4B5563] mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#4B5563] mb-2">
                        Job Title
                      </label>
                      <input
                        type="text"
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#4B5563] mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#4B5563] mb-2">
                      LinkedIn Profile
                      <span className="text-[#9CA3AF] font-normal ml-1">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={linkedinUrl}
                      onChange={(e) => setLinkedinUrl(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent"
                      placeholder="www.linkedin.com/in/yourname"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#4B5563] mb-2">
                      Bio
                    </label>
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent resize-none"
                      placeholder="Tell us about yourself..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] disabled:opacity-50 transition"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              )}

              {activeTab === 'notifications' && (
                <form onSubmit={handleUpdateNotifications} className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-[#1F2937] mb-4">Notification Preferences</h2>
                    <p className="text-[#6B7280] mb-6">Choose how you want to be notified</p>
                  </div>

                  <div className="space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailOnFeedback}
                        onChange={(e) => setEmailOnFeedback(e.target.checked)}
                        className="mt-1 w-5 h-5 text-[#8CAFA8] rounded focus:ring-2 focus:ring-[#8CAFA8]"
                      />
                      <div>
                        <div className="font-medium text-[#1F2937]">Feedback Received</div>
                        <div className="text-sm text-[#6B7280]">Get notified when someone submits feedback</div>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailOnReminder}
                        onChange={(e) => setEmailOnReminder(e.target.checked)}
                        className="mt-1 w-5 h-5 text-[#8CAFA8] rounded focus:ring-2 focus:ring-[#8CAFA8]"
                      />
                      <div>
                        <div className="font-medium text-[#1F2937]">Reminder Emails</div>
                        <div className="text-sm text-[#6B7280]">Receive updates about pending requests</div>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailWeeklyDigest}
                        onChange={(e) => setEmailWeeklyDigest(e.target.checked)}
                        className="mt-1 w-5 h-5 text-[#8CAFA8] rounded focus:ring-2 focus:ring-[#8CAFA8]"
                      />
                      <div>
                        <div className="font-medium text-[#1F2937]">Weekly Digest</div>
                        <div className="text-sm text-[#6B7280]">Get a summary of your feedback activity</div>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailMarketing}
                        onChange={(e) => setEmailMarketing(e.target.checked)}
                        className="mt-1 w-5 h-5 text-[#8CAFA8] rounded focus:ring-2 focus:ring-[#8CAFA8]"
                      />
                      <div>
                        <div className="font-medium text-[#1F2937]">Product Updates</div>
                        <div className="text-sm text-[#6B7280]">News, tips, and feature announcements</div>
                      </div>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] disabled:opacity-50 transition"
                  >
                    {loading ? 'Saving...' : 'Save Preferences'}
                  </button>
                </form>
              )}

              {activeTab === 'billing' && (
                <div>
                  <h2 className="text-xl font-semibold text-[#1F2937] mb-4">Billing & Subscription</h2>
                  <p className="text-[#6B7280] mb-6">Manage your subscription and payment methods</p>

                  <div className="bg-[#F0F4F3] border border-blue-200 rounded-lg p-6 mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-blue-900 capitalize">{subscription?.plan || 'Free'} Plan</h3>
                        <p className="text-sm text-[#7A9B94]">
                          {subscription?.status === 'active' ? 'Active' : subscription?.status || 'Active'}
                          {subscription?.currentPeriodEnd && !subscription?.cancelAtPeriodEnd && ` • Renews ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`}
                          {subscription?.currentPeriodEnd && subscription?.cancelAtPeriodEnd && ` • Active until ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`}
                        </p>
                        {subscription?.cancelAtPeriodEnd && (
                          <div className="mt-2 p-3 bg-orange-100 border border-orange-300 rounded-lg">
                            <p className="text-sm text-orange-800 font-medium">
                              Cancellation Scheduled
                            </p>
                            <p className="text-xs text-orange-700 mt-1">
                              Your subscription will end on {subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'the end of your billing period'}. After that, you'll be downgraded to the Free plan. You can resubscribe at any time.
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-3">
                        {subscription?.plan !== 'free' ? (
                          <button
                            onClick={handleManageBilling}
                            disabled={loadingPortal}
                            className="bg-[#8CAFA8] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#7A9B94] disabled:opacity-50 transition flex items-center gap-2"
                          >
                            {loadingPortal ? 'Loading...' : (
                              <>
                                Manage Subscription
                                <ExternalLink className="w-4 h-4" />
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => navigate('/pricing')}
                            className="bg-[#8CAFA8] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#7A9B94] transition"
                          >
                            Upgrade Plan
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {subscription?.plan === 'free' && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-[#1F2937]">Upgrade to unlock more features</h3>
                      <ul className="space-y-2 text-sm text-[#6B7280]">
                        <li className="flex items-start gap-2">
                          <span className="text-[#8CAFA8] mt-0.5">✓</span>
                          <span>More feedback requests per month</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#8CAFA8] mt-0.5">✓</span>
                          <span>Unlimited AI-powered analysis</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#8CAFA8] mt-0.5">✓</span>
                          <span>PDF exports and custom branding</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#8CAFA8] mt-0.5">✓</span>
                          <span>Team collaboration features</span>
                        </li>
                      </ul>
                      <button
                        onClick={() => navigate('/pricing')}
                        className="text-[#8CAFA8] hover:text-[#7A9B94] font-semibold"
                      >
                        View all plans →
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'security' && (
                <div>
                  <h2 className="text-xl font-semibold text-[#1F2937] mb-4">Security Settings</h2>
                  <p className="text-[#6B7280] mb-6">Manage your account security</p>
                  
                  <div className="space-y-4">
                    <div className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-[#1F2937]">Email Address</h3>
                          <p className="text-sm text-[#6B7280]">{profile?.email}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-[#1F2937]">Password</h3>
                          <p className="text-sm text-[#6B7280]">Last changed 30 days ago</p>
                        </div>
                        <button className="text-[#8CAFA8] hover:text-[#7A9B94] font-semibold">
                          Change Password
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
    </>
  );
}
